import {
  ResponsiveContainer, ComposedChart, Bar, Line, LineChart, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { ChartCard } from './charts';
import { fmtMonth, fmtCurrencyWhole } from '../lib/fmt';
import type { PlanLineRow } from '../lib/api';

// Charts off the computed plan rollup (department_id IS NULL). Total cost uses
// cost.fixed.total (the rollup of the individual fixed lines) to avoid double
// counting; op profit prefers the engine's op_profit line, falling back to
// revenue − cost.
function buildSeries(lines: PlanLineRow[]) {
  const rollup = lines.filter((l) => l.department_id === null);
  const months = Array.from(new Set(rollup.map((l) => l.period_month))).sort();
  const idx = new Map<string, number>();
  for (const l of rollup) idx.set(`${l.line_code}|${l.period_month}`, l.amount);
  const v = (code: string, m: string) => idx.get(`${code}|${m}`) ?? 0;
  return months.map((m) => {
    const revenue = v('rev.tm', m) + v('rev.ff', m) + v('rev.sub', m);
    const cost = v('cost.salary', m) + v('cost.var.labor', m) + v('cost.var.materials', m) + v('cost.fixed.total', m);
    const opProfit = idx.has(`op_profit|${m}`) ? v('op_profit', m) : revenue - cost;
    return { month: fmtMonth(m), revenue: Math.round(revenue), cost: Math.round(cost), opProfit: Math.round(opProfit), cash: Math.round(v('cash.closing', m)) };
  });
}

export function PlanPnlCharts({ lines }: { lines: PlanLineRow[] }) {
  const data = buildSeries(lines);
  if (data.length === 0) return null;
  const fmt = (v: number) => fmtCurrencyWhole(v);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12, padding: 12 }}>
      <ChartCard title="Revenue · Cost · Operating profit" sub="by month">
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} />
            <YAxis tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} width={52} tickFormatter={(v) => fmtCurrencyWhole(v)} />
            <Tooltip formatter={(v: number) => fmt(v)} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="revenue" name="Revenue" fill="var(--color-green)" />
            <Bar dataKey="cost" name="Cost" fill="var(--color-amber)" />
            <Line dataKey="opProfit" name="Operating profit" stroke="var(--color-blue)" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard title="Cash balance" sub="ending cash by month">
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} />
            <YAxis tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} width={52} tickFormatter={(v) => fmtCurrencyWhole(v)} />
            <Tooltip formatter={(v: number) => fmt(v)} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
            <Line dataKey="cash" name="Ending cash" stroke="var(--color-green)" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

// ─── Variance trend (actual vs baseline) ─────────────────────────────────────

interface VarianceRow {
  category: string;
  cells: Array<{ period_month: string; actual: number; baselines: Record<string, number> }>;
}

export function VarianceTrendChart({ rows, periods, primaryId }: { rows: VarianceRow[]; periods: string[]; primaryId: string | null }) {
  const find = (cat: string) => rows.find((r) => r.category === cat);
  const rev = find('revenue'); const op = find('operating_profit');
  const cellAt = (row: VarianceRow | undefined, m: string) => row?.cells.find((c) => c.period_month === m);
  const data = periods.map((m) => {
    const rc = cellAt(rev, m); const oc = cellAt(op, m);
    return {
      month: fmtMonth(m),
      revActual: rc ? Math.round(rc.actual) : 0,
      revBase: rc && primaryId ? Math.round(rc.baselines[primaryId] ?? 0) : 0,
      opActual: oc ? Math.round(oc.actual) : 0,
      opBase: oc && primaryId ? Math.round(oc.baselines[primaryId] ?? 0) : 0,
    };
  });
  if (data.length === 0) return null;
  return (
    <div style={{ padding: 12 }}>
      <ChartCard title="Actual vs baseline" sub="revenue & operating profit (dashed = baseline)" height={240}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} />
            <YAxis tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} width={52} tickFormatter={(v) => fmtCurrencyWhole(v)} />
            <Tooltip formatter={(v: number) => fmtCurrencyWhole(v)} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line dataKey="revActual" name="Revenue (actual)" stroke="var(--color-green)" strokeWidth={2} dot={false} />
            <Line dataKey="revBase" name="Revenue (baseline)" stroke="var(--color-green)" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
            <Line dataKey="opActual" name="Op profit (actual)" stroke="var(--color-blue)" strokeWidth={2} dot={false} />
            <Line dataKey="opBase" name="Op profit (baseline)" stroke="var(--color-blue)" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
